import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, ChevronDown, ChevronRight, Loader, RefreshCw, ListOrdered } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import DailyStrip from './DailyStrip';

// THE FROG CARD - the one thing on the screen.
//
// What it replaces: a To-Do page opening on 54 tasks, 23 habits, 125 backlog items and a 7-item
// inbox, where four tasks were completed in the preceding fortnight. The list was never the
// product; deciding was, and nothing was deciding. So this card answers one question - what now -
// and puts everything else one click away.
//
// ⚠️ THE REASON LINE IS NOT DECORATION. A frog with no stated reason is one he overrules, and an
// overruled frog teaches the ranking nothing. It is rendered at the same weight as the task.
//
// ⚠️ IT NEVER RENDERS A SPINNER OVER THE LAST GOOD ANSWER. This sits at the top of a page he
// opens all day; flashing "Loading..." where the task was is how a screen stops being trusted.

const CARD = {
  background: 'linear-gradient(180deg,#fffdf7 0%,#fff9f2 100%)',
  border: '2px solid var(--orange)',
  borderRadius: 12,
  padding: '18px 20px',
  marginBottom: 18,
  boxShadow: '0 2px 10px rgba(220,88,42,0.10)',
};

const BTN = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 700,
  background: bg, border: '1px solid ' + border, color, cursor: 'pointer', fontFamily: 'inherit',
});

// Called directly rather than through neonQuery, and the response deliberately has no `rows`
// key: neonQuery returns data.rows whenever that key exists and DISCARDS everything else, which
// is how a handler returning an envelope shipped a blank page here on 2026-09-11.
async function callFrog(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/frog', {
    method: 'POST', headers, body: JSON.stringify({ action, ...params }),
  });
  // 403 means somebody else is looking at this page. Not an error to them - the card simply
  // is not theirs - so it is signalled separately and the card renders nothing at all.
  if (res.status === 403) { const e = new Error('not yours'); e.notMine = true; throw e; }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'frog ' + res.status);
  return res.json();
}

export default function FrogCard({ onChanged, onPlan, dailyItems = [] }) {
  const [state, setState] = useState({ frog: null, onDeck: [], poolSize: 0, scheduledHabitIds: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [notMine, setNotMine] = useState(false);
  const [showDeck, setShowDeck] = useState(false);
  const [asking, setAsking] = useState(false);   // "Not today" reason prompt
  const [why, setWhy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await callFrog('today');
      setState({ frog: r.frog, onDeck: r.onDeck || [], poolSize: r.poolSize, scheduledHabitIds: r.scheduledHabitIds || [] });
      setErr(null);
    } catch (e) { if (e.notMine) setNotMine(true); else setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Every mutation returns the NEXT frog from the server, so the card never has to guess what
  // comes after and can never briefly show a task that is already closed.
  const act = async (action, params) => {
    setBusy(true);
    try {
      const r = await callFrog(action, params);
      setState(s => ({ ...s, frog: r.frog, onDeck: r.onDeck || [],
        scheduledHabitIds: r.scheduledHabitIds || s.scheduledHabitIds }));
      setAsking(false); setWhy('');
      if (onChanged) onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const f = state.frog;
  const isHabit = f && f.srcTable === 'greg_daily_templates';

  const strip = (
    <DailyStrip
      items={dailyItems}
      scheduledIds={state.scheduledHabitIds}
      onSchedule={i => act('order_add', { srcTable: 'greg_daily_templates', srcId: String(i.id) })}
    />
  );


  // Somebody else is on the page. Render nothing rather than an error they cannot act on.
  if (notMine) return null;

  if (loading) {
    return (
      <div style={{ ...CARD, color: 'var(--gray-500)', fontSize: 13 }}>
        <Loader size={14} className="spin" /> Working out what matters most...
      </div>
    );
  }

  if (err) {
    // Says what broke and leaves the rest of the page alone. A dead frog card must never take
    // the board down with it - the lists below still work.
    return (
      <div style={{ ...CARD, border: '1px solid var(--gray-300)', background: '#fafafa' }}>
        <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>
          Could not work out your frog ({err}). Your lists below are unaffected.
          <button onClick={load} style={{ ...BTN('#fff', 'var(--gray-300)', 'var(--navy)'), marginLeft: 10, padding: '4px 10px', fontSize: 11 }}>
            <RefreshCw size={11} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!f) {
    return (
      <>
      <div style={{ ...CARD, borderColor: 'var(--green)', background: '#f3fbf5' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#276749' }}>Nothing left that is yours to start.</div>
        <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4 }}>
          Everything open is parked on somebody else or already done today.
        </div>
      </div>
      {strip}
      </>
    );
  }

  return (
    <>
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>🐸</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: 'var(--orange)' }}>
          {/* A habit he scheduled first is genuinely next, but it is not a frog, and calling a
              glass of water one is how the word stops meaning anything. */}
          {isHabit ? 'NEXT UP' : 'EAT THIS FROG'}
        </span>
        {f.chosenBy === 'greg' && (
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: '#eef2f7', padding: '2px 7px', borderRadius: 10 }}>
            YOUR PICK
          </span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>
          {f.domain}{f.effortMinutes ? ' · about ' + f.effortMinutes + ' min' : ''}
        </span>
      </div>

      <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.3, marginBottom: 7 }}>
        {f.title}
      </div>

      {/* Why this one. Same visual weight as the task, on purpose. */}
      <div style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.5, marginBottom: 14 }}>
        {f.reason}
      </div>

      {asking ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>In a word, why not today?</span>
          <input
            autoFocus value={why} onChange={e => setWhy(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && why.trim()) act('skip', { srcTable: f.srcTable, srcId: f.srcId, reason: why.trim() });
              if (e.key === 'Escape') { setAsking(false); setWhy(''); }
            }}
            placeholder="blocked / too big / not now / dead"
            style={{ padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minWidth: 220 }}
          />
          <button disabled={!why.trim() || busy} onClick={() => act('skip', { srcTable: f.srcTable, srcId: f.srcId, reason: why.trim() })}
            style={{ ...BTN('var(--navy)', 'var(--navy)', '#fff'), opacity: why.trim() && !busy ? 1 : 0.5 }}>
            Skip it
          </button>
          <button onClick={() => { setAsking(false); setWhy(''); }}
            style={{ ...BTN('transparent', 'transparent', 'var(--gray-500)'), padding: '7px 6px' }}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={() => act('done', { srcTable: f.srcTable, srcId: f.srcId })}
            style={{ ...BTN('var(--green,#2f855a)', 'var(--green,#2f855a)', '#fff'), opacity: busy ? 0.6 : 1 }}>
            <Check size={15} /> Done
          </button>
          <button disabled={busy} onClick={() => setAsking(true)}
            style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>
            <X size={14} /> Not today
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={() => onPlan && onPlan()} title="See the master list and set your own order"
            style={{ ...BTN('transparent', 'transparent', 'var(--gray-600)'), padding: '6px 8px', fontSize: 12 }}>
            <ListOrdered size={13} /> Order my day
          </button>
          <button onClick={() => setShowDeck(v => !v)}
            style={{ ...BTN('transparent', 'transparent', 'var(--navy)'), padding: '6px 4px', fontSize: 12 }}>
            {showDeck ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Up next ({state.onDeck.length})
          </button>
        </div>
      )}

      {/* On deck. Names only - detail here would rebuild the wall of text this replaces. */}
      {showDeck && state.onDeck.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #f0c9b4' }}>
          {state.onDeck.map(d => (
            <div key={d.srcTable + d.srcId}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13, color: 'var(--gray-800)' }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.title}
              </span>
              <button disabled={busy} onClick={() => act('swap', { srcTable: d.srcTable, srcId: d.srcId })}
                title="Make this the frog instead"
                style={{ ...BTN('#fff', 'var(--gray-300)', 'var(--navy)'), padding: '3px 9px', fontSize: 11 }}>
                Do this instead
              </button>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>
            {state.poolSize} things are open and yours. The rest are below.
          </div>
        </div>
      )}
    </div>
    {strip}
    </>
  );
}
